import { useRef, useState } from 'react';
import { carsToCsv } from '../lib/csv';
import { defaultSettings } from '../lib/defaults';
import { today } from '../lib/format';
import { exportJson, parseImport } from '../lib/storage';
import { useStore } from '../state/store';
import { DealForm } from './DealForm';
import { NumberField } from './NumberField';

function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function SettingsPage() {
  const { data, setSettings, replaceAll, eraseAll, saved } = useStore();
  const s = data.settings;
  const file = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState('');
  const sc = s.scenarios;

  const onImport = async (f: File | undefined) => {
    if (!f) return;
    const result = parseImport(await f.text());
    if (!result.ok) {
      setMessage(result.error);
    } else if (window.confirm(`Replace everything in the app with this file (${result.data.cars.length} cars)? Export first if you want a copy of what is here now.`)) {
      replaceAll(result.data);
      setMessage(`Imported ${result.data.cars.length} cars.`);
    }
    if (file.current) file.current.value = '';
  };

  return (
    <div className="page narrow">
      <h1>Settings</h1>
      {!saved && <p className="error" role="alert">Your browser would not save data. Changes will be lost when you close this page. Export a backup below.</p>}

      <section className="card">
        <h2>Your targets</h2>
        <p className="muted">A deal is "worth doing" only if it meets all three.</p>
        <div className="grid">
          <NumberField label="Profit at least" prefix="£" value={s.targets.minProfit} onChange={(n) => setSettings({ ...s, targets: { ...s.targets, minProfit: n } })} />
          <NumberField label="Return on cash at least" suffix="%" value={s.targets.minRoiPct} onChange={(n) => setSettings({ ...s, targets: { ...s.targets, minRoiPct: n } })} />
          <NumberField label="Profit per day at least" prefix="£" value={s.targets.minProfitPerDay} onChange={(n) => setSettings({ ...s, targets: { ...s.targets, minProfitPerDay: n } })} />
          <NumberField label="Cash set aside for cars" prefix="£" value={s.startingCash} onChange={(n) => setSettings({ ...s, startingCash: n })} />
        </div>
      </section>

      <section className="card">
        <h2>"If things go differently" cases</h2>
        <p className="muted">How much worse (or better) than planned. Use negative numbers for lower prices or fewer days.</p>
        <h3>Cautious</h3>
        <div className="grid">
          <NumberField label="Sale price change" suffix="%" min={-100} value={sc.cautious.salePricePct} onChange={(n) => setSettings({ ...s, scenarios: { ...sc, cautious: { ...sc.cautious, salePricePct: n } } })} />
          <NumberField label="Repairs change" suffix="%" min={-100} value={sc.cautious.repairsPct} onChange={(n) => setSettings({ ...s, scenarios: { ...sc, cautious: { ...sc.cautious, repairsPct: n } } })} />
          <NumberField label="Extra days" min={-60} value={sc.cautious.extraDays} onChange={(n) => setSettings({ ...s, scenarios: { ...sc, cautious: { ...sc.cautious, extraDays: n } } })} />
        </div>
        <h3>Good</h3>
        <div className="grid">
          <NumberField label="Sale price change" suffix="%" min={-100} value={sc.good.salePricePct} onChange={(n) => setSettings({ ...s, scenarios: { ...sc, good: { ...sc.good, salePricePct: n } } })} />
          <NumberField label="Repairs change" suffix="%" min={-100} value={sc.good.repairsPct} onChange={(n) => setSettings({ ...s, scenarios: { ...sc, good: { ...sc.good, repairsPct: n } } })} />
          <NumberField label="Extra days" min={-60} value={sc.good.extraDays} onChange={(n) => setSettings({ ...s, scenarios: { ...sc, good: { ...sc.good, extraDays: n } } })} />
        </div>
        <button type="button" className="btn small" onClick={() => setSettings({ ...s, scenarios: defaultSettings().scenarios, targets: defaultSettings().targets })}>Reset targets and cases</button>
      </section>

      <section className="card">
        <h2>Starting values for new cars</h2>
        <p className="muted">Costs you usually have, so you do not retype them. Purchase and sale price are best left at 0.</p>
        <DealForm deal={s.defaults} onChange={(defaults) => setSettings({ ...s, defaults })} />
      </section>

      <section className="card">
        <h2>Your data</h2>
        <p>Everything is stored in this browser only: no account, nothing is sent anywhere. Clearing site data deletes it, so export a backup now and then.</p>
        <div className="actions">
          <button type="button" className="btn" onClick={() => download(`car-flip-planner-${today()}.json`, exportJson(data), 'application/json')}>Export backup (JSON)</button>
          <button type="button" className="btn" onClick={() => download(`car-flip-cars-${today()}.csv`, carsToCsv(data.cars), 'text/csv')}>Export cars (CSV)</button>
          <label className="btn" htmlFor="import-file">Import backup…</label>
          <input id="import-file" ref={file} className="sr-only" type="file" accept="application/json,.json" onChange={(e) => void onImport(e.target.files?.[0])} />
        </div>
        {message && <p role="status">{message}</p>}
        <div className="actions">
          <button type="button" className="btn danger" onClick={() => { if (window.confirm('Delete every car and reset all settings? This cannot be undone.')) { eraseAll(); setMessage('Everything was erased.'); } }}>Erase everything</button>
        </div>
      </section>
    </div>
  );
}
